import type {
  GetPaymentParams,
  GetPaymentResponse,
  Payment,
  PaymentSessionResponse,
} from "@/services/api/payment";
import PaymentService from "@/services/api/payment";
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

interface PaymentState {
  payments: Payment[];
  totalPayments: number;
  isFetching: boolean;
  paymentSession: PaymentSessionResponse | null;
  isCreatingSession: boolean;
  sessionError: string | null;
}

const name = "payment";

const initialState: PaymentState = {
  payments: [],
  totalPayments: 0,
  isFetching: false,
  paymentSession: null,
  isCreatingSession: false,
  sessionError: null,
};

// Async thunk
export const getPayments = createAsyncThunk<
  GetPaymentResponse, // return type
  GetPaymentParams, // argument type
  { rejectValue: string }
>(`${name}/getPayments`, async (params, { rejectWithValue }) => {
  try {
    const data: GetPaymentResponse =
      await PaymentService.getAllPayments(params);
    return data ?? { results: [], totalRecord: 0 };
  } catch (err) {
    const error = err as { message?: string };
    return rejectWithValue(error.message ?? "Failed to fetch payments");
  }
});

export const createPaymentSession = createAsyncThunk<
  PaymentSessionResponse, // return type
  void, // argument type
  { rejectValue: string }
>(`${name}/createPaymentSession`, async (_, { rejectWithValue }) => {
  try {
    const response = await PaymentService.createPaymentSession();
    return response.data;
  } catch (err) {
    const error = err as { message?: string };
    return rejectWithValue(
      error.message ?? "Failed to create payment session",
    );
  }
});

const paymentSlice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => initialState,
    clearPaymentSession: (state) => {
      state.paymentSession = null;
      state.sessionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPayments.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        getPayments.fulfilled,
        (state, action: PayloadAction<GetPaymentResponse>) => {
          state.isFetching = false;
          state.payments = action.payload.results;
          state.totalPayments = action.payload.totalRecord;
        },
      )
      .addCase(getPayments.rejected, (state, action) => {
        state.isFetching = false;
        console.error("Failed to fetch payments:", action.payload);
      })
      .addCase(createPaymentSession.pending, (state) => {
        state.isCreatingSession = true;
        state.sessionError = null;
      })
      .addCase(
        createPaymentSession.fulfilled,
        (state, action: PayloadAction<PaymentSessionResponse>) => {
          state.isCreatingSession = false;
          state.paymentSession = action.payload;
          state.sessionError = null;
        },
      )
      .addCase(createPaymentSession.rejected, (state, action) => {
        state.isCreatingSession = false;
        state.sessionError = action.payload as string;
      });
  },
});

export default paymentSlice.reducer;
export const { clear, clearPaymentSession } = paymentSlice.actions;
export type { PaymentState };
